/**
 * =======================================================
 * SISTEMA DE PREFETCH DE COMPONENTES E DADOS
 * =======================================================
 *
 * O prefetch de ROTAS (páginas) agora é feito globalmente
 * em App.tsx via useGlobalRoutePrefetch (idle-time, tiered).
 *
 * Este hook cuida de:
 * 1. SUB-COMPONENTES pesados (modais, editors)
 * 2. Prefetch on-hover de chunks
 * 3. Prefetch on-hover de DADOS (React Query)
 *
 * -------------------------------------------------------
 * COMO ADICIONAR UMA NOVA FERRAMENTA:
 * -------------------------------------------------------
 *
 * 1. PREFETCH DE ROTA → Adicionar em App.tsx (tier1 ou tier2)
 *
 * 2. PREFETCH DE SUB-COMPONENTES PESADOS:
 *    → Adicionar em useComponentPrefetch() abaixo
 *
 * 3. PREFETCH ON HOVER (prefetchPageComponent):
 *    → Adicionar case no switch abaixo
 *    → Incluir sub-componentes pesados da página
 *
 * 4. PREFETCH ON HOVER DE DADOS (prefetchRouteData):
 *    → Adicionar case no switch com queryClient.prefetchQuery
 *
 * =======================================================
 */

import { useEffect, useRef } from 'react';
import type { QueryClient } from '@tanstack/react-query';

const SUB_COMPONENT_DELAY = 2000;

/**
 * Prefetches heavy sub-components (not routes — those are in App.tsx).
 */
export function useComponentPrefetch() {
  const hasPrefetched = useRef(false);

  useEffect(() => {
    if (hasPrefetched.current) return;
    hasPrefetched.current = true;

    const timer = setTimeout(() => {
      const run = () => {
        import('@/components/minha-forja/forja-notes/ForjaNotesApp');
        import('@/components/minha-forja/forja-tasks/ForjaTasksApp');
      };
      if ('requestIdleCallback' in window) {
        (window as Window).requestIdleCallback(run, { timeout: 10000 });
      } else {
        run();
      }
    }, SUB_COMPONENT_DELAY);

    return () => clearTimeout(timer);
  }, []);
}

/**
 * Prefetch a specific page's sub-components on hover.
 */
export function prefetchPageComponent(toolId: string) {
  switch (toolId) {
    case 'minha-forja':
      import('@/components/minha-forja/forja-notes/ForjaNotesApp');
      import('@/components/minha-forja/forja-tasks/ForjaTasksApp');
      break;
    case 'marketplace':
      import('@/pages/marketplace/ListingDetails');
      import('@/pages/marketplace/Negotiations');
      import('@/pages/marketplace/NegotiationChat');
      import('@/pages/marketplace/SellerPlans');
      break;
    case 'parceiros-forja':
      import('@/pages/parceiros/PartnerDetail');
      import('@/pages/parceiros/PartnerAdmin');
      import('@/pages/parceiros/CampaignDetail');
      break;
    case 'command-terminal':
      import('@/pages/admin/ModoForjaAdmin');
      import('@/pages/admin/MarketplaceAdmin');
      import('@/pages/admin/DesafiosAdmin');
      import('@/pages/admin/ToolsAdmin');
      import('@/pages/admin/NotificationsAdmin');
      import('@/pages/admin/ParceirosAdmin');
      break;
    case 'comunidade':
      import('@/pages/Comunidade');
      break;
  }
}

/**
 * Prefetch critical React Query data for a route on hover.
 * Complements prefetchPageComponent (JS chunks) by warming data cache.
 * Uses dynamic imports to avoid pulling service modules into the navbar chunk.
 */
export async function prefetchRouteData(toolId: string, queryClient: QueryClient) {
  const STALE = 5 * 60 * 1000;

  switch (toolId) {
    case 'comunidade': {
      const { fetchCommunityTabs } = await import('@/services/community');
      queryClient.prefetchQuery({ queryKey: ['community-tabs'], queryFn: fetchCommunityTabs, staleTime: STALE });
      break;
    }
    case 'marketplace': {
      const { fetchListings } = await import('@/services/marketplace');
      queryClient.prefetchQuery({ queryKey: ['marketplace-listings', '{}'], queryFn: () => fetchListings({}), staleTime: STALE });
      break;
    }
    case 'minha-forja': {
      const [{ fetchFolders, fetchNotes }, { fetchWorkspaces }] = await Promise.all([
        import('@/services/forjaNotes'),
        import('@/services/forjaTasks'),
      ]);
      queryClient.prefetchQuery({ queryKey: ['forja-notes-folders'], queryFn: fetchFolders, staleTime: STALE });
      queryClient.prefetchQuery({ queryKey: ['forja-notes'], queryFn: fetchNotes, staleTime: STALE });
      queryClient.prefetchQuery({ queryKey: ['forja-tasks-workspaces'], queryFn: fetchWorkspaces, staleTime: STALE });
      break;
    }
    case 'command-terminal': {
      const { fetchTerminalMetrics } = await import('@/services/branding');
      queryClient.prefetchQuery({ queryKey: ['terminal-metrics'], queryFn: fetchTerminalMetrics, staleTime: 60 * 60 * 1000 });
      break;
    }
    case 'arsenal': {
      const { fetchArsenalVideos } = await import('@/services/arsenal');
      queryClient.prefetchQuery({ queryKey: ['arsenal-videos', '{}'], queryFn: () => fetchArsenalVideos(), staleTime: STALE });
      break;
    }
    case 'parceiros-forja': {
      const { fetchActivePartners } = await import('@/services/partners');
      queryClient.prefetchQuery({ queryKey: ['active-partners'], queryFn: fetchActivePartners, staleTime: STALE });
      break;
    }
  }
}
